import { CommunityPermission } from "@outverse-video/outverse-api-client-lib";
import { community, permissions as communityPerms } from "app/stores/community";
import { derived } from "svelte/store";

interface WithPermissions {
  permissions: string[] | null;
}

/**
 * Returns true if `permissions` has the specified `permission`
 */
export function hasPermission(
  permissions?: WithPermissions | string[],
  permission?: string
): boolean {
  let perms: string[] = [];
  if (!permissions) return false;

  if (Array.isArray(permissions)) {
    perms = permissions;
  } else {
    perms = permissions.permissions || [];
  }
  return perms.some((perm) => perm === permission);
}

export const canReceiveAdminNotifications = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.CanReceiveAdminNotifications)
);
export const isCommunityMember = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.AccessCommunityServer)
);

/** `True` if the community is *public* or user is a member of *private* community */
export const canUserViewCommunity = derived(
  community,
  ($community) => !$community.isPrivate || $community.accessType === "full"
);

export const canInviteMembers = derived(
  [isCommunityMember, communityPerms, community],
  ([$isCommunityMember, $permissions, $community]) =>
    (!$community.isPrivate && $isCommunityMember) ||
    ($community.isPrivate && hasPermission($permissions, CommunityPermission.InviteMembers))
);

export const canManageTeamMembers = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.ManageTeamMembers)
);

export const canRemoveMembers = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.RemoveCommunityMember)
);
export const canEditCommunitySettings = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.ChangeCommunity)
);
export const canViewContextSources = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.ViewContextSources)
);
export const canViewAnalytics = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.CanViewAnalytics)
);
// currently an alias for admin features
export const isAdmin = canEditCommunitySettings;
export const canReceiveCommunityAdminNotifications = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.CanReceiveAdminNotifications)
);
export const canViewConversationHistory = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.CanViewConversationHistory)
);

export const canAddForumSpace = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.AddForumspaceCommunity)
);
export const canAddCustomSpace = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.AddCustomspaceCommunity)
);
export const canAddContentLink = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.AddContentlinkCommunity)
);

export const canEditContentLink = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.ChangeContentlinkCommunity)
);

export const canDeleteContentLink = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.DeleteContentlinkCommunity)
);

export const canAddSpacegroup = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.AddSpacegroup)
);
export const canChangeSpaceGroup = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.ChangeSpacegroup)
);
export const canDeleteSpaceGroup = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.DeleteSpacegroup)
);
export const canChangeForumSpace = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.ChangeForumspaceCommunity)
);
export const canChangeCustomSpace = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.ChangeCustomspaceCommunity)
);
export const canDeleteForumSpace = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.DeleteForumspaceCommunity)
);
export const canDeleteCustomSpace = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.DeleteCustomspaceCommunity)
);

export const canAddFolder = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.AddDocumentCommunity)
);
export const canAddDocument = derived(communityPerms, ($permissions) =>
  hasPermission($permissions, CommunityPermission.AddDocumentCommunity)
);

export const isTeamMember = derived(
  [canAddFolder, canAddDocument, canAddForumSpace],
  ([$canAddFolder, $canAddDocument, $canAddForumSpace]) =>
    $canAddFolder && $canAddDocument && $canAddForumSpace
);
